import { isEqual } from "es-toolkit";

import type {
  ConditionBuild,
  ConditionDesign,
  EffectDesign,
  HandlerDesign,
  ResponseDesign,
  StateAction,
  TriggerDesign,
} from "@skilitics/nautilus-core-designer-data";

const hasMatchingIds = <T extends { id: number }>(oldItems: T[], newItems: T[]): boolean => {
  const oldIds = new Set(oldItems.map((item) => item.id));
  return newItems.some((item) => oldIds.has(item.id));
};

const addConditionActions = (
  to: { responseId: number } | { slotId: { parentId: number; name?: string } },
  condition: ConditionDesign,
): StateAction[] => {
  return [{ type: "ADD_CONDITION", payload: { to, condition } }];
};

const addResponseActions = (handlerId: number, response: ResponseDesign): StateAction[] => {
  const actions: StateAction[] = [
    { type: "ADD_RESPONSE", payload: { handlerId, id: response.id, label: response.label } },
  ];
  for (const effect of response.effects) {
    actions.push({ type: "ADD_EFFECT", payload: { responseId: response.id, effect } });
  }
  if (response.condition) {
    actions.push(...addConditionActions({ responseId: response.id }, response.condition));
  }
  return actions;
};

const addHandlerActions = (triggerId: number, handler: HandlerDesign): StateAction[] => {
  const actions: StateAction[] = [
    { type: "ADD_HANDLER", payload: { triggerId, id: handler.id, label: handler.label, cases: handler.cases } },
  ];
  for (const response of handler.responses) {
    actions.push(...addResponseActions(handler.id, response));
  }
  return actions;
};

export const addTriggerActions = (nodeId: number, trigger: TriggerDesign): StateAction[] => {
  const actions: StateAction[] = [
    {
      type: "ADD_TRIGGER",
      payload: {
        nodeId,
        id: trigger.id,
        cases: trigger.cases,
        label: trigger.label,
      },
    },
  ];
  for (const handler of trigger.handlers) {
    actions.push(...addHandlerActions(trigger.id, handler));
  }
  return actions;
};

const diffConditionChildren = (oldCondition: ConditionBuild, newCondition: ConditionBuild): StateAction[] => {
  const actions: StateAction[] = [];

  const allSlotNames = new Set([...Object.keys(oldCondition.children), ...Object.keys(newCondition.children)]);

  for (const slotName of allSlotNames) {
    const oldChild = oldCondition.children[slotName];
    const newChild = newCondition.children[slotName];

    if (!oldChild && !newChild) continue;

    const oldArray = !oldChild ? [] : Array.isArray(oldChild) ? oldChild : [oldChild];
    const newArray = !newChild ? [] : Array.isArray(newChild) ? newChild : [newChild];

    const oldById = new Map(oldArray.map((c) => [c.id, c]));
    const newById = new Map(newArray.map((c) => [c.id, c]));

    if (oldArray.length > 0 && newArray.length > 0 && !hasMatchingIds(oldArray, newArray)) {
      // No matching IDs in this slot — remove all old, add all new
      for (const old of oldArray) {
        actions.push({ type: "REMOVE_CONDITION", payload: { id: old.id } });
      }
      for (const cond of newArray) {
        actions.push(...addConditionActions({ slotId: { parentId: newCondition.id, name: slotName } }, cond));
      }
      continue;
    }

    // Remove old conditions not in new
    for (const [id] of oldById) {
      if (!newById.has(id)) {
        actions.push({ type: "REMOVE_CONDITION", payload: { id } });
      }
    }

    for (const [id, newCond] of newById) {
      const oldCond = oldById.get(id);
      if (!oldCond) {
        actions.push(...addConditionActions({ slotId: { parentId: newCondition.id, name: slotName } }, newCond));
        continue;
      }
      // Same ID — diff recursively
      actions.push(...diffCondition(oldCond, newCond));
    }
  }

  return actions;
};

const diffCondition = (oldCondition: ConditionDesign, newCondition: ConditionDesign): StateAction[] => {
  if (isEqual(oldCondition, newCondition)) return [];

  if (oldCondition.id !== newCondition.id) {
    // Different root IDs — replace entirely
    return [
      { type: "REMOVE_CONDITION", payload: { id: oldCondition.id } },
      // Re-add at the same location is handled by caller
    ];
  }

  const actions: StateAction[] = [];

  // Same ID — check config
  if (!isEqual(oldCondition.config, newCondition.config)) {
    actions.push({ type: "UPDATE_CONDITION_CONFIG", payload: { id: oldCondition.id, config: newCondition.config } });
  }

  // Diff children
  actions.push(...diffConditionChildren(oldCondition, newCondition));

  return actions;
};

const diffResponseCondition = (
  responseId: number,
  oldCondition: ConditionDesign | undefined,
  newCondition: ConditionDesign | undefined,
): StateAction[] => {
  if (isEqual(oldCondition, newCondition)) return [];

  if (oldCondition && !newCondition) {
    return [{ type: "REMOVE_CONDITION", payload: { id: oldCondition.id } }];
  }

  if (!oldCondition && newCondition) {
    return addConditionActions({ responseId }, newCondition);
  }

  // Both exist
  if (oldCondition!.id !== newCondition!.id) {
    return [
      { type: "REMOVE_CONDITION", payload: { id: oldCondition!.id } },
      ...addConditionActions({ responseId }, newCondition!),
    ];
  }

  return diffCondition(oldCondition!, newCondition!);
};

const diffEffects = (responseId: number, oldEffects: EffectDesign[], newEffects: EffectDesign[]): StateAction[] => {
  if (isEqual(oldEffects, newEffects)) return [];

  const actions: StateAction[] = [];
  const oldById = new Map(oldEffects.map((e) => [e.id, e]));
  const newById = new Map(newEffects.map((e) => [e.id, e]));

  // Remove old effects not in new
  for (const [id] of oldById) {
    if (!newById.has(id)) {
      actions.push({ type: "REMOVE_EFFECT", payload: { id } });
    }
  }

  for (const [id, newEffect] of newById) {
    const oldEffect = oldById.get(id);
    if (!oldEffect) {
      actions.push({ type: "ADD_EFFECT", payload: { responseId, effect: newEffect } });
      continue;
    }
    // Same ID — check config
    if (!isEqual(oldEffect.config, newEffect.config)) {
      actions.push({ type: "UPDATE_EFFECT_CONFIG", payload: { id, config: newEffect.config } });
    }
  }

  return actions;
};

const diffResponses = (
  handlerId: number,
  oldResponses: ResponseDesign[],
  newResponses: ResponseDesign[],
): StateAction[] => {
  if (isEqual(oldResponses, newResponses)) return [];

  const actions: StateAction[] = [];
  const oldById = new Map(oldResponses.map((r) => [r.id, r]));
  const newById = new Map(newResponses.map((r) => [r.id, r]));

  // Remove old responses not in new
  for (const [id] of oldById) {
    if (!newById.has(id)) {
      actions.push({ type: "REMOVE_RESPONSE", payload: { id } });
    }
  }

  for (const [id, newResponse] of newById) {
    const oldResponse = oldById.get(id);
    if (!oldResponse) {
      actions.push(...addResponseActions(handlerId, newResponse));
      continue;
    }

    // Same ID — check if anything matches underneath
    if (
      oldResponse.effects.length > 0 &&
      newResponse.effects.length > 0 &&
      !hasMatchingIds(oldResponse.effects, newResponse.effects)
    ) {
      // No matching effects — remove and readd
      actions.push({ type: "REMOVE_RESPONSE", payload: { id } });
      actions.push(...addResponseActions(handlerId, newResponse));
      continue;
    }

    // Update label if changed
    if (oldResponse.label !== newResponse.label) {
      actions.push({ type: "EDIT_RESPONSE", payload: { id, label: newResponse.label } });
    }

    // Diff effects
    actions.push(...diffEffects(id, oldResponse.effects, newResponse.effects));

    // Diff condition
    actions.push(...diffResponseCondition(id, oldResponse.condition, newResponse.condition));
  }

  return actions;
};

const diffHandlers = (triggerId: number, oldHandlers: HandlerDesign[], newHandlers: HandlerDesign[]): StateAction[] => {
  if (isEqual(oldHandlers, newHandlers)) return [];

  const actions: StateAction[] = [];
  const oldById = new Map(oldHandlers.map((h) => [h.id, h]));
  const newById = new Map(newHandlers.map((h) => [h.id, h]));

  // Remove old handlers not in new
  for (const [id] of oldById) {
    if (!newById.has(id)) {
      actions.push({ type: "REMOVE_HANDLER", payload: { id } });
    }
  }

  for (const [id, newHandler] of newById) {
    const oldHandler = oldById.get(id);
    if (!oldHandler) {
      actions.push(...addHandlerActions(triggerId, newHandler));
      continue;
    }

    // Same ID — check if responses share any IDs
    if (
      oldHandler.responses.length > 0 &&
      newHandler.responses.length > 0 &&
      !hasMatchingIds(oldHandler.responses, newHandler.responses)
    ) {
      // No matching responses — remove and readd
      actions.push({ type: "REMOVE_HANDLER", payload: { id } });
      actions.push(...addHandlerActions(triggerId, newHandler));
      continue;
    }

    // Update handler properties if changed
    if (oldHandler.label !== newHandler.label || !isEqual(oldHandler.cases, newHandler.cases)) {
      actions.push({ type: "UPDATE_HANDLER", payload: { id, label: newHandler.label, cases: newHandler.cases } });
    }

    // Diff responses
    actions.push(...diffResponses(id, oldHandler.responses, newHandler.responses));
  }

  return actions;
};

export const diffTriggerDesigns = (
  nodeId: number,
  oldTriggers: TriggerDesign[],
  newTriggers: TriggerDesign[],
): StateAction[] => {
  if (isEqual(oldTriggers, newTriggers)) return [];

  const actions: StateAction[] = [];
  const oldById = new Map(oldTriggers.map((t) => [t.id, t]));
  const newById = new Map(newTriggers.map((t) => [t.id, t]));

  // Remove old triggers not in new
  for (const [id] of oldById) {
    if (!newById.has(id)) {
      actions.push({ type: "REMOVE_TRIGGER", payload: { id } });
    }
  }

  for (const [id, newTrigger] of newById) {
    const oldTrigger = oldById.get(id);
    if (!oldTrigger) {
      actions.push(...addTriggerActions(nodeId, newTrigger));
      continue;
    }

    // Same ID — check if handlers share any IDs
    if (
      oldTrigger.handlers.length > 0 &&
      newTrigger.handlers.length > 0 &&
      !hasMatchingIds(oldTrigger.handlers, newTrigger.handlers)
    ) {
      // No matching handlers — remove and readd
      actions.push({ type: "REMOVE_TRIGGER", payload: { id } });
      actions.push(...addTriggerActions(nodeId, newTrigger));
      continue;
    }

    // Cases change requires remove-and-readd if structurally different
    if (!isEqual(oldTrigger.cases, newTrigger.cases)) {
      actions.push({ type: "UPDATE_TRIGGER", payload: { id, label: newTrigger.label, cases: newTrigger.cases } });
    } else if (oldTrigger.label !== newTrigger.label) {
      actions.push({ type: "UPDATE_TRIGGER", payload: { id, label: newTrigger.label } });
    }

    // Diff handlers
    actions.push(...diffHandlers(id, oldTrigger.handlers, newTrigger.handlers));
  }

  return actions;
};
